import { useEffect, useMemo, useState } from 'react';
import { Plus, Box, Search, LayoutGrid, List, ChevronDown, ChevronRight, Clock } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { useSandboxStore } from '@/stores/useSandboxStore';
import { usePoolStore } from '@/stores/usePoolStore';
import { useAuth } from '@/contexts/AuthContext';
import { CheckoutWizard } from '@/components/CheckoutWizard';
import { SandboxCard } from '@/components/SandboxCard';
import { Sandbox, SandboxState } from '@/types';
import { Link } from 'react-router-dom';
import { formatShape, formatRelativeTime, getTimeRemaining, getStateClass } from '@/utils/format';

const STATE_FILTERS: Array<{ label: string; value: SandboxState | 'All' }> = [
  { label: 'All', value: 'All' },
  { label: 'Running', value: 'Running' },
  { label: 'Terminated', value: 'Terminated' },
  { label: 'Failed', value: 'Failed' },
];

export function SandboxesPage() {
  const { sandboxes, isLoading, fetchSandboxes, deleteSandbox, getSandboxesByPool } =
    useSandboxStore();
  const { selectedPoolIds, getSelectedPools, getPoolById } = usePoolStore();
  const { isAdmin } = useAuth();

  const [showWizard, setShowWizard] = useState(false);
  const [stateFilter, setStateFilter] = useState<SandboxState | 'All'>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [collapsedPools, setCollapsedPools] = useState<Set<string>>(new Set());

  const selectedPools = getSelectedPools();

  // Fetch sandboxes for all selected pools
  useEffect(() => {
    for (const poolId of selectedPoolIds) {
      fetchSandboxes(poolId);
    }
  }, [selectedPoolIds, fetchSandboxes]);

  // Toggle section collapse
  const toggleCollapse = (poolId: string) => {
    setCollapsedPools(prev => {
      const next = new Set(prev);
      if (next.has(poolId)) {
        next.delete(poolId);
      } else {
        next.add(poolId);
      }
      return next;
    });
  };

  // Filter sandboxes applying state + search across all selected pools
  const filterSandboxes = (list: Sandbox[]): Sandbox[] => {
    let filtered = list;

    if (stateFilter !== 'All') {
      filtered = filtered.filter((sb) => sb.state === stateFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter((sb) => sb.sandboxName.toLowerCase().includes(q));
    }

    return filtered;
  };

  // All sandboxes across selected pools (unfiltered, for counts)
  const allSandboxes = useMemo(() => {
    return selectedPoolIds.flatMap(poolId => getSandboxesByPool(poolId));
  }, [selectedPoolIds, sandboxes, getSandboxesByPool]);

  // Filtered sandboxes across all selected pools
  const filteredSandboxes = useMemo(() => {
    return filterSandboxes(allSandboxes);
  }, [allSandboxes, stateFilter, searchQuery]);

  // Grouped by pool (for multi-pool view)
  const groupedByPool = useMemo(() => {
    return selectedPoolIds.map(poolId => {
      const pool = getPoolById(poolId);
      const poolSandboxes = getSandboxesByPool(poolId);
      const filtered = filterSandboxes(poolSandboxes);
      return { poolId, pool, sandboxes: filtered, total: poolSandboxes.length };
    }).filter(g => g.pool); // Only include groups for pools that exist
  }, [selectedPoolIds, sandboxes, stateFilter, searchQuery, getSandboxesByPool, getPoolById]);

  const isMultiPool = selectedPoolIds.length > 1;

  const handleDelete = async (sandboxId: string, name: string) => {
    // Find which pool this sandbox belongs to
    const sandbox = allSandboxes.find(sb => sb.sandboxId === sandboxId);
    if (!sandbox) return;
    if (!confirm(`Terminate sandbox "${name}"?`)) return;

    try {
      await deleteSandbox(sandbox.poolId, sandboxId);
      toast.success('Sandbox terminated');
    } catch {
      toast.error('Failed to terminate sandbox');
    }
  };

  // Count by state for filter badges
  const stateCounts = {
    All: allSandboxes.length,
    Running: allSandboxes.filter((s) => s.state === 'Running').length,
    Creating: allSandboxes.filter((s) => s.state === 'Creating').length,
    Failed: allSandboxes.filter((s) => s.state === 'Failed').length,
  };

  // Subtitle text
  const subtitle = (() => {
    if (selectedPools.length === 0) return 'No pool selected';
    if (selectedPools.length === 1) return selectedPools[0].displayName;
    return `${selectedPools.length} pools selected`;
  })();

  // Render sandbox grid for a list of sandboxes
  const renderGrid = (items: Sandbox[]) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {items.map((sandbox) => (
        <SandboxCard
          key={sandbox.sandboxId}
          sandbox={sandbox}
          onDelete={handleDelete}
          showDelete={isAdmin}
        />
      ))}
    </div>
  );

  // Render sandbox list/table for a list of sandboxes
  const renderList = (items: Sandbox[]) => (
    <div className="card overflow-hidden">
      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Image</th>
            <th>State</th>
            <th>Shape</th>
            <th>Expires</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {items.map((sandbox) => {
            const remaining = getTimeRemaining(sandbox.expiresAt);
            return (
              <tr key={sandbox.sandboxId}>
                <td>
                  <Link
                    to={`/sandboxes/${sandbox.sandboxId}`}
                    className="font-medium text-brand-500 hover:text-brand-400"
                  >
                    {sandbox.sandboxName}
                  </Link>
                </td>
                <td>
                  <span className="text-surface-500 text-xs font-mono">{sandbox.imageId}</span>
                </td>
                <td>
                  <span className={getStateClass(sandbox.state)}>{sandbox.state}</span>
                </td>
                <td className="text-surface-500 text-xs">
                  {formatShape(sandbox.shape.processorCount, sandbox.shape.memorySize)}
                </td>
                <td>
                  {remaining ? (
                    <span className={clsx('inline-flex items-center gap-1 text-xs', remaining.isUrgent ? 'text-danger' : 'text-surface-500')}>
                      <Clock className="w-3.5 h-3.5" />
                      {remaining.display}
                    </span>
                  ) : (
                    <span className="text-surface-400">&mdash;</span>
                  )}
                </td>
                <td className="text-surface-500 text-xs">{formatRelativeTime(sandbox.createdAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 animate-stagger-1">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-surface-900 dark:text-surface-100">
              Sandboxes
            </h1>
            <p className="text-sm text-surface-500 dark:text-surface-400 mt-0.5">
              {subtitle}
            </p>
          </div>
          {isAdmin && (
            <button onClick={() => setShowWizard(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-1.5" />
              New Sandbox
            </button>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="mb-5 flex flex-wrap items-center gap-3 animate-stagger-2">
        {/* State pill filters */}
        <div className="flex items-center gap-1 bg-surface-100 dark:bg-white/[0.04] rounded-full p-0.5">
          {STATE_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStateFilter(f.value)}
              className={clsx(
                'px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-150',
                stateFilter === f.value
                  ? 'bg-white dark:bg-white/[0.12] text-surface-900 dark:text-surface-100 shadow-sm'
                  : 'text-surface-500 dark:text-surface-400 hover:text-surface-700 dark:hover:text-surface-200'
              )}
            >
              {f.label}
              {stateCounts[f.value as keyof typeof stateCounts] > 0 && (
                <span className={clsx(
                  'ml-1.5 text-[10px]',
                  stateFilter === f.value
                    ? 'text-surface-500 dark:text-surface-400'
                    : 'text-surface-400 dark:text-surface-500'
                )}>
                  {stateCounts[f.value as keyof typeof stateCounts]}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="flex-1" />

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-8 py-1.5 text-sm w-48"
          />
        </div>

        {/* View toggle */}
        <div className="flex items-center bg-surface-100 dark:bg-white/[0.04] rounded-fluent-md p-0.5">
          <button
            onClick={() => setViewMode('grid')}
            className={clsx(
              'p-1.5 rounded-fluent transition-colors',
              viewMode === 'grid'
                ? 'bg-white dark:bg-white/[0.12] text-surface-900 dark:text-surface-100 shadow-sm'
                : 'text-surface-400 hover:text-surface-600 dark:hover:text-surface-300'
            )}
            title="Grid view"
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={clsx(
              'p-1.5 rounded-fluent transition-colors',
              viewMode === 'list'
                ? 'bg-white dark:bg-white/[0.12] text-surface-900 dark:text-surface-100 shadow-sm'
                : 'text-surface-400 hover:text-surface-600 dark:hover:text-surface-300'
            )}
            title="List view"
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      {isLoading && allSandboxes.length === 0 ? (
        <div className="empty-state">
          <div className="spinner" />
          <p className="mt-3 text-sm text-surface-500">Loading...</p>
        </div>
      ) : filteredSandboxes.length === 0 ? (
        <div className="empty-state">
          <div className="bg-surface-100 dark:bg-white/[0.06] p-4 rounded-fluent-xl mb-4">
            <Box className="w-10 h-10 text-surface-400" />
          </div>
          <h3 className="text-base font-semibold text-surface-900 dark:text-surface-100 mb-1">
            {stateFilter !== 'All' || searchQuery.trim() ? 'No matches' : 'No sandboxes'}
          </h3>
          <p className="text-sm text-surface-500 mb-5 max-w-xs">
            {stateFilter !== 'All' || searchQuery.trim()
              ? 'No sandboxes match the current filters.'
              : 'Create your first sandbox to get started.'}
          </p>
          {isAdmin && stateFilter === 'All' && !searchQuery.trim() && (
            <button onClick={() => setShowWizard(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-1.5" />
              New Sandbox
            </button>
          )}
        </div>
      ) : isMultiPool ? (
        /* ---- Grouped by Pool (multi-pool) ---- */
        <div className="space-y-5">
          {groupedByPool.map(({ poolId, pool, sandboxes: poolFiltered }) => {
            if (!pool) return null;
            const isCollapsed = collapsedPools.has(poolId);
            return (
              <div key={poolId}>
                {/* Section header */}
                <button
                  onClick={() => toggleCollapse(poolId)}
                  className="flex items-center gap-2 mb-3 group"
                >
                  {isCollapsed ? (
                    <ChevronRight className="w-4 h-4 text-surface-400 group-hover:text-surface-600 dark:group-hover:text-surface-300 transition-colors" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-surface-400 group-hover:text-surface-600 dark:group-hover:text-surface-300 transition-colors" />
                  )}
                  <span className="text-sm font-semibold text-surface-900 dark:text-surface-100">
                    {pool.displayName}
                  </span>
                  <span className="text-xs text-surface-400 bg-surface-100 dark:bg-white/[0.06] rounded-full px-2 py-0.5">
                    {poolFiltered.length}
                  </span>
                </button>

                {/* Section content */}
                {!isCollapsed && (
                  poolFiltered.length === 0 ? (
                    <p className="text-sm text-surface-400 ml-6 mb-2">No sandboxes match filters.</p>
                  ) : viewMode === 'grid' ? (
                    renderGrid(poolFiltered)
                  ) : (
                    renderList(poolFiltered)
                  )
                )}
              </div>
            );
          })}
        </div>
      ) : viewMode === 'grid' ? (
        /* ---- Card Grid View (single pool) ---- */
        renderGrid(filteredSandboxes)
      ) : (
        /* ---- List View (single pool) ---- */
        renderList(filteredSandboxes)
      )}

      {showWizard && <CheckoutWizard onClose={() => setShowWizard(false)} />}
    </div>
  );
}
